import { filter } from '@prismicio/client'
import prismicUtils from "../utils/prismicUtils";
import stringUtils from "../utils/stringUtils";
import { RichText } from "@/compat/prismic";
import prismicContentTypes from "../constants/prismicContentTypes";

const itemsPerPage = 10;

const fetchCategories = async (sectionName) => {

    const prismicApi = await prismicUtils.getApiForSection(sectionName);

    const response = await prismicApi.get(
        {
            filters: [
                filter.missing("my.category.parent"),
                filter.not("document.tags", ["precedents"])
            ],
            pageSize: 100
        }
    );

    const { results } = response;
    const mappedResults = results
        .map((item) => ({
            id: item.id,
            title: item.data.title[0].text,
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

    return mappedResults;
};

const fetchCategoriesByParentId = async (sectionName, parentId) => {
    const prismicApi = await prismicUtils.getApiForSection(sectionName);
    const response = await prismicApi.get(
        {
            filters: [
                filter.at("my.category.parent", parentId),
                filter.not("document.tags", ["precedents"]),
            ],
            pageSize: 100
        }
    );

    const { results } = response;
    const mappedResults = results
        .map((item) => ({
            id: item.id,
            title: item.data.title[0].text,
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

    return mappedResults;
};

const fetchCategory = async (sectionName, categoryId) => {
    const prismicApi = await prismicUtils.getApiForSection(sectionName);
    const response = await prismicApi.getByID(categoryId);

    const mappedResponse = {
        id: response.id,
        title: response.data.title[0].text,
    };

    return mappedResponse;
};

const fetchCategoryArticles = async (sectionName, categoryId, pageNumber, searchTerm) => {
    const ordering = "my.post.post_title";
    const predicate = "my.post.category";

    const prismicApi = await prismicUtils.getApiForSection(sectionName);

    const response = await prismicApi.get(
        {
            filters: searchTerm
                ? filter.fulltext(ordering, searchTerm)
                : filter.at(predicate, categoryId),
            pageSize: itemsPerPage,
            page: pageNumber,
            orderings: [{ field: ordering }],
        }
    );

    const pagedResponse = createPagedResponse(response);

    return pagedResponse;
};

const fetchCategoryWebinars = async (sectionName, categoryId, pageNumber, searchTerm) => {
    const ordering = "my.webinar.title";
    const predicate = "my.webinar.category";

    const prismicApi = await prismicUtils.getApiForSection(sectionName);

    const response = await prismicApi.get(
        {
            filters: searchTerm
                ? filter.fulltext(ordering, searchTerm)
                : filter.at(predicate, categoryId),
            pageSize: itemsPerPage,
            page: pageNumber,
            orderings: [{ field: ordering }],
        }
    );

    const pagedResponse = createPagedResponse(response);

    return pagedResponse;
};

const fetchCategoryPrecedents = async (sectionName, categoryId, pageNumber, searchTerm) => {
    const ordering = "my.precedent.title";
    const predicate = "my.precedent.category";

    const prismicApi = await prismicUtils.getApiForSection(sectionName);

    const response = await prismicApi.get(
        {
            filters: searchTerm
                ? filter.fulltext(ordering, searchTerm)
                : filter.at(predicate, categoryId),
            pageSize: itemsPerPage,
            page: pageNumber,
            orderings: [{ field: ordering }],
        }
    );

    const pagedResponse = createPagedResponse(response);

    return pagedResponse;
};

const fetchCategoryDownloads = async (sectionName, categoryId, pageNumber, searchTerm) => {
    const ordering = "my.download.title";
    const predicate = "my.download.category";

    const prismicApi = await prismicUtils.getApiForSection(sectionName);

    const response = await prismicApi.get(
        {
            filters: searchTerm
                ? filter.fulltext(ordering, searchTerm)
                : filter.at(predicate, categoryId),
            pageSize: itemsPerPage,
            page: pageNumber,
            orderings: [{ field: ordering }],
        }       
    );

    const pagedResponse = createPagedResponse(response);

    return pagedResponse;
};

const fetchContentItemById = async (sectionName, contentItemId) => {
    const prismicApi = await prismicUtils.getApiForSection(sectionName);

    const response = await prismicApi.getByID(contentItemId);

    const mappedResponse = extractItemData(response);

    return mappedResponse;
};

const createPagedResponse = (response) => {
    const mappedResults = response.results.map(extractItemData);

    const mappedResponse = {
        results: mappedResults,
        totalPages: response.total_pages,
        totalResults: response.total_results_size,
    };

    return mappedResponse;
};

// v2 "page" bodies are a slices array (e.g. `text` slices), not a rich-text field.
// Pull the rich-text out of each slice's `primary` (by common field name, else the
// first array-valued field) and concatenate the blocks into one rich-text field.
const extractSliceRichText = (body) => {
    if (!Array.isArray(body)) return null;
    const blocks = [];
    for (const slice of body) {
        const primary = slice?.primary ?? {};
        const richText =
            primary.text ??
            primary.content ??
            primary.rich_text ??
            Object.values(primary).find((v) => Array.isArray(v));
        if (Array.isArray(richText)) blocks.push(...richText);
    }
    return blocks.length ? blocks : null;
};

const extractItemData = (item) => {
    switch (item.type) {
        case "post": {
            const fullExcerpt = RichText.asText(
                item.data?.body[0]?.primary.content_editor
            );
            return {
                id: item.id,
                title: RichText.asText(item.data.post_title),
                excerpt: stringUtils.trimTextToLength(fullExcerpt),
                content: item.data?.body[0]?.primary.content_editor,
                type: prismicContentTypes.article,
            };
        }
        case "webinar": {
            const fullExcerpt = RichText.asText(item.data?.intro_text);
            return {
                id: item.id,
                title: RichText.asText(item.data.title),
                excerpt: stringUtils.trimTextToLength(fullExcerpt),
                content: item.data.body,
                embed: item.data.video,
                type: prismicContentTypes.webinar,
                introText: item.data.intro_text,
                downloadUrl: item.data?.attachment?.url,
            };
        }
        case "precedent": {
            const fullExcerpt = RichText.asText(
                item.data?.body[0]?.primary?.content_editor
            );

            return {
                id: item.id,
                title: RichText.asText(item.data.title),
                excerpt: stringUtils.trimTextToLength(fullExcerpt),
                content: item.data?.body[0]?.primary?.content_editor,
                type: prismicContentTypes.precedent,
                downloadUrl: item.data?.attachment?.url,
            };
        }
        case "download": {
            return {
                id: item.id,
                title: RichText.asText(item.data.title),
                previewImageUrl: item.data.file_preview.url,
                downloadUrl: item.data.upload_media.url,
                type: prismicContentTypes.download,
            };
        }
        case "page": {
            // Prismic v1 nested the fields under a `page` group with `.value` /
            // `["non-repeat"]`; @prismicio/client v7 (v2 API) returns them flat.
            // Support both so LEI renders regardless of the document's shape.
            const v1 = item.data?.page;
            if (v1) {
                return {
                    id: item.id,
                    title: v1.title?.value,
                    bodyContent: v1.body?.value?.[0]?.["non-repeat"]?.content?.value,
                };
            }
            return {
                id: item.id,
                title: item.data?.title,
                bodyContent: extractSliceRichText(item.data?.body) ?? item.data?.content,
            };
        }
        default:
            throw new Error(`Unrecognised prismic content type: ${item.type}`);
    }
};

const prismicService = {
    fetchCategory,
    fetchCategories,
    fetchCategoriesByParentId,
    fetchCategoryArticles,
    fetchCategoryWebinars,
    fetchCategoryPrecedents,
    fetchCategoryDownloads,
    fetchContentItemById,
};

export default prismicService;
